// Plan-review contract types + pure helpers, shared by both chat surfaces
// (MarketplaceTwinChat and AskNaturalOrganicChat) and by PlanReviewCard.
// Kept separate from the component so the component file only exports a
// component (react-refresh rule) and the helpers stay unit-testable.

/** One step of the research plan, as returned by the backend. */
export interface PlanReviewStep {
  id: string;
  label: string;
  /** Semantic step kind (`ask`, `filter`, …) → the label shown above the step text.
   *  Absent on user-added steps and on plans from before the backend sent it. */
  kind?: string | null;
}

/**
 * What each step kind is called in the card.
 *
 * The step text itself is the EXACT question sent to the twins (backend #448), which on its own
 * reads as a stray second-person question — nothing tells the reader it is put to the twins
 * rather than to them. This names the step's role above it.
 *
 * The words live here, not in the backend, so changing them is a frontend deploy rather than a
 * backend deploy + Celery restart. An unmapped kind renders no label — never a guess.
 */
export const STEP_KIND_LABELS: Record<string, string> = {
  ask: 'Ask the consumer twins',
  filter: 'Pull the panel',
  narrow: 'Narrow the group',
  schema: "Check what's on file",
  premise: 'Check the premise',
  compute: 'Crunch the numbers',
};

/** The ask step is the substantive one — teal; supporting steps stay muted grey so the
 *  question doesn't compete with the plumbing around it. */
const ASK_KIND = 'ask';

export const stepKindLabel = (kind?: string | null): string | null =>
  (kind && STEP_KIND_LABELS[kind]) || null;

export const stepKindIsAsk = (kind?: string | null): boolean => kind === ASK_KIND;

/** The `result.plan_review` payload on a `format: "plan_review"` chat turn. */
export interface PlanReviewPayload {
  steps: PlanReviewStep[];
  eta_minutes_min?: number;
  eta_minutes_max?: number;
}

/** Anything carrying the optional ETA pair (plan_review or structured_progress). */
export interface EtaRange {
  eta_minutes_min?: number;
  eta_minutes_max?: number;
}

/** `format` value marking a plan-review turn. */
export const PLAN_REVIEW_FORMAT = 'plan_review';

/** Approving the plan = sending this as a normal chat message; the backend resumes. */
export const APPROVE_PLAN_MESSAGE = 'go';

const CHANGE_MESSAGE_PREFIX = 'Please update the plan:';

/** "≈ 6–8 min" / "≈ 5 min", or null when no ETA is known. */
export const formatEtaRange = (min?: number, max?: number): string | null => {
  if (min == null && max == null) return null;
  const lo = min ?? max;
  const hi = max ?? min;
  return lo === hi ? `≈ ${lo} min` : `≈ ${lo}–${hi} min`;
};

// Past this multiple of the max estimate, the run has clearly overrun and the static
// "Estimated 6–8 min" copy reads as a stuck/broken run — switch to an honest overrun line.
export const ETA_OVERRUN_FACTOR = 1.5;

/** Running-state copy derived from the approved plan's ETA, or null when unknown.
 *  Once `elapsedMs` passes ETA_OVERRUN_FACTOR × the max estimate, returns a "running
 *  longer than estimated" line instead of the (now misleading) fixed estimate. */
export const planEtaRunCopy = (min?: number, max?: number, elapsedMs?: number): string | null => {
  const range = formatEtaRange(min, max);
  if (!range) return null;
  if (max && elapsedMs != null && elapsedMs > max * 60_000 * ETA_OVERRUN_FACTOR) {
    return 'Taking longer than estimated — still working, hang tight.';
  }
  return `Estimated ${range} for this plan.`;
};

/**
 * The ETA governing the CURRENT run: prefer a live `structured_progress` ETA when
 * the backend sends one, else the ETA from the plan the user approved.
 */
export const resolveRunEta = (
  progress: EtaRange | null | undefined,
  plan: EtaRange | null | undefined,
): EtaRange | null => {
  if (progress && (progress.eta_minutes_min != null || progress.eta_minutes_max != null)) {
    return progress;
  }
  if (plan && (plan.eta_minutes_min != null || plan.eta_minutes_max != null)) {
    return plan;
  }
  return null;
};

/**
 * Deterministic change description sent when the user edits the plan:
 * the full final plan, numbered, plus what was removed/added.
 * e.g. `Please update the plan: 1) A 2) B (removed: C; added: B)`
 */
export const composePlanChangeMessage = (
  finalSteps: PlanReviewStep[],
  removedLabels: string[],
  addedLabels: string[],
): string => {
  const numbered = finalSteps.map((s, i) => `${i + 1}) ${s.label}`).join(' ');
  const clauses: string[] = [];
  if (removedLabels.length > 0) clauses.push(`removed: ${removedLabels.join(', ')}`);
  if (addedLabels.length > 0) clauses.push(`added: ${addedLabels.join(', ')}`);
  const suffix = clauses.length > 0 ? ` (${clauses.join('; ')})` : '';
  return `${CHANGE_MESSAGE_PREFIX} ${numbered}${suffix}`;
};
